import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Upload, Link2, Github, CheckCircle2, FileText, Trash2, Plus, Send } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Progress } from '@/components/ui/progress';
import ConfirmModal from '@/components/shared/ConfirmModal';

const pastSubmissions = [
  { id: 1, title: 'Chatbot with OpenAI API', module: 'Module 1', date: '2026-04-15', status: 'graded', score: 92, feedback: 'Excellent work! The conversation flow is very natural.' },
  { id: 2, title: 'Prompt Engineering Challenge', module: 'Module 2', date: '2026-04-28', status: 'graded', score: 88, feedback: 'Great prompts! Try to include more edge case handling.' },
  { id: 3, title: 'RAG Pipeline Implementation', module: 'Module 3', date: '2026-05-05', status: 'under-review', score: null, feedback: null },
];

const statusConfig = {
  graded: { label: 'Graded', color: 'bg-green-100 text-green-700' },
  'under-review': { label: 'Under Review', color: 'bg-primary/10 text-primary' },
  pending: { label: 'Pending', color: 'bg-blue-100 text-blue-700' },
};

export default function ProjectSubmission() {
  const [tab, setTab] = useState('submit');
  const [form, setForm] = useState({ title: '', module: '', description: '', github: '', liveUrl: '', techStack: '' });
  const [files, setFiles] = useState([]);
  const [submitted, setSubmitted] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  const handleFileChange = (e) => {
    const newFiles = Array.from(e.target.files);
    setFiles(prev => [...prev, ...newFiles]);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const dropped = Array.from(e.dataTransfer.files);
    setFiles(prev => [...prev, ...dropped]);
  };

  const removeFile = (i) => setFiles(prev => prev.filter((_, idx) => idx !== i));

  const isValid = form.title && form.module && form.description;

  const handleSubmit = () => {
    setSubmitting(true);
    setTimeout(() => {
      setSubmitting(false);
      setShowConfirm(false);
      setSubmitted(true);
    }, 1200);
  };

  const resetForm = () => {
    setForm({ title: '', module: '', description: '', github: '', liveUrl: '', techStack: '' });
    setFiles([]);
    setSubmitted(false);
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="font-heading text-2xl font-bold tracking-tight">Project Submission</h2>
        <p className="text-muted-foreground text-sm mt-1">Submit your projects for review and grading</p>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-black/5 p-1 rounded-2xl">
        {[{ id: 'submit', label: 'Submit Project' }, { id: 'history', label: 'My Submissions' }].map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            className={`flex-1 px-4 py-2 rounded-xl text-xs font-semibold transition-all ${tab === t.id ? 'bg-white shadow-sm text-foreground' : 'text-muted-foreground hover:text-foreground'}`}>
            {t.label}
          </button>
        ))}
      </div>

      <AnimatePresence mode="wait">
        <motion.div key={tab} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -5 }} transition={{ duration: 0.18 }}>

          {tab === 'submit' && (
            submitted ? (
              <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }}
                className="glass rounded-3xl border border-white/80 p-12 text-center space-y-4">
                <motion.div animate={{ scale: [1, 1.15, 1] }} transition={{ duration: 0.5 }}
                  className="w-20 h-20 rounded-3xl bg-green-100 flex items-center justify-center mx-auto">
                  <CheckCircle2 className="w-10 h-10 text-green-500" />
                </motion.div>
                <h3 className="font-heading font-bold text-xl text-green-700">Project Submitted!</h3>
                <p className="text-sm text-muted-foreground">Your project has been submitted for review. You'll receive feedback within 2–3 business days.</p>
                <Button onClick={resetForm} variant="outline" className="rounded-xl border-black/15 font-semibold gap-2">
                  <Plus className="w-4 h-4" /> Submit Another
                </Button>
              </motion.div>
            ) : (
              <div className="space-y-4">
                {/* Project Info */}
                <div className="glass rounded-2xl border border-white/80 p-6 space-y-4">
                  <h3 className="font-heading font-bold text-base">Project Details</h3>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-xs font-semibold">Project Title <span className="text-red-500">*</span></Label>
                      <Input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
                        placeholder="e.g. RAG-based Q&A Bot" className="mt-1.5 rounded-xl border-black/15 text-sm" />
                    </div>
                    <div>
                      <Label className="text-xs font-semibold">Module <span className="text-red-500">*</span></Label>
                      <select value={form.module} onChange={e => setForm(f => ({ ...f, module: e.target.value }))}
                        className="mt-1.5 w-full rounded-xl border border-black/15 text-sm px-3 py-2 bg-background font-body">
                        <option value="">Select module…</option>
                        <option value="Module 1">Module 1 – GenAI Foundations</option>
                        <option value="Module 2">Module 2 – Prompt Engineering</option>
                        <option value="Module 3">Module 3 – LLM Architecture & APIs</option>
                        <option value="Module 4">Module 4 – RAG & Vector DBs</option>
                        <option value="Module 5">Module 5 – Fine-tuning & Deployment</option>
                        <option value="Module 6">Module 6 – Capstone</option>
                      </select>
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs font-semibold">Project Description <span className="text-red-500">*</span></Label>
                    <Textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                      placeholder="Describe what your project does, the problem it solves, and how you built it..."
                      className="mt-1.5 rounded-xl border-black/15 text-sm h-28 resize-none" />
                  </div>
                  <div>
                    <Label className="text-xs font-semibold">Tech Stack Used</Label>
                    <Input value={form.techStack} onChange={e => setForm(f => ({ ...f, techStack: e.target.value }))}
                      placeholder="e.g. Python, LangChain, ChromaDB, FastAPI" className="mt-1.5 rounded-xl border-black/15 text-sm" />
                  </div>
                </div>

                {/* Links */}
                <div className="glass rounded-2xl border border-white/80 p-6 space-y-4">
                  <h3 className="font-heading font-bold text-base">Project Links</h3>
                  <div className="space-y-3">
                    <div className="relative">
                      <Github className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input value={form.github} onChange={e => setForm(f => ({ ...f, github: e.target.value }))}
                        placeholder="GitHub Repository URL" className="pl-9 rounded-xl border-black/15 text-sm" />
                    </div>
                    <div className="relative">
                      <Link2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input value={form.liveUrl} onChange={e => setForm(f => ({ ...f, liveUrl: e.target.value }))}
                        placeholder="Live Demo URL (optional)" className="pl-9 rounded-xl border-black/15 text-sm" />
                    </div>
                  </div>
                </div>

                {/* File Upload */}
                <div className="glass rounded-2xl border border-white/80 p-6 space-y-4">
                  <h3 className="font-heading font-bold text-base">Attach Files</h3>
                  <div
                    onDragOver={e => { e.preventDefault(); setDragOver(true); }}
                    onDragLeave={() => setDragOver(false)}
                    onDrop={handleDrop}
                    className={`border-2 border-dashed rounded-2xl p-8 text-center transition-all ${dragOver ? 'border-primary bg-primary/5' : 'border-black/15 hover:border-primary/40 hover:bg-primary/3'}`}
                  >
                    <Upload className={`w-8 h-8 mx-auto mb-3 transition-colors ${dragOver ? 'text-primary' : 'text-muted-foreground'}`} />
                    <p className="text-sm font-semibold text-foreground">Drag & drop files here</p>
                    <p className="text-xs text-muted-foreground mt-1">or</p>
                    <label className="mt-3 inline-block cursor-pointer">
                      <span className="text-xs font-bold text-primary hover:underline">Browse Files</span>
                      <input type="file" multiple className="hidden" onChange={handleFileChange} />
                    </label>
                    <p className="text-[10px] text-muted-foreground mt-2">PDF, ZIP, PY, IPYNB up to 50MB each</p>
                  </div>

                  {files.length > 0 && (
                    <div className="space-y-2">
                      {files.map((file, i) => (
                        <motion.div key={i} initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }}
                          className="flex items-center gap-3 p-3 rounded-xl bg-white/60 border border-black/8">
                          <FileText className="w-4 h-4 text-primary flex-shrink-0" />
                          <span className="text-xs font-medium flex-1 truncate">{file.name}</span>
                          <span className="text-[10px] text-muted-foreground">{(file.size / 1024).toFixed(1)} KB</span>
                          <button onClick={() => removeFile(i)} className="text-muted-foreground hover:text-red-500 transition-colors">
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </motion.div>
                      ))}
                    </div>
                  )}
                </div>

                <Button onClick={() => isValid && setShowConfirm(true)} disabled={!isValid}
                  className="w-full gap-2 rounded-xl bg-primary text-primary-foreground font-bold shadow-lg shadow-primary/25 hover:bg-primary/90 h-11">
                  <Send className="w-4 h-4" /> Submit Project for Review
                </Button>
              </div>
            )
          )}

          {tab === 'history' && (
            <div className="space-y-4">
              {/* Stats */}
              <div className="grid grid-cols-3 gap-3">
                {[
                  { label: 'Total Submitted', value: pastSubmissions.length },
                  { label: 'Graded', value: pastSubmissions.filter(s => s.status === 'graded').length },
                  { label: 'Avg Score', value: `${Math.round(pastSubmissions.filter(s => s.score).reduce((a, s) => a + s.score, 0) / pastSubmissions.filter(s => s.score).length)}%` },
                ].map((stat, i) => (
                  <motion.div key={i} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.07 }}
                    className="glass rounded-2xl border border-white/80 p-4 text-center">
                    <p className="font-heading font-black text-2xl">{stat.value}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">{stat.label}</p>
                  </motion.div>
                ))}
              </div>

              <div className="space-y-3">
                {pastSubmissions.map((sub, i) => {
                  const cfg = statusConfig[sub.status];
                  return (
                    <motion.div key={sub.id} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.08 }}
                      className="glass rounded-2xl border border-white/80 p-5 space-y-3">
                      <div className="flex flex-wrap items-start justify-between gap-3">
                        <div>
                          <h3 className="font-heading font-bold text-sm">{sub.title}</h3>
                          <p className="text-xs text-muted-foreground mt-0.5">{sub.module} · Submitted {new Date(sub.date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          {sub.score !== null && (
                            <span className="font-heading font-black text-lg text-primary">{sub.score}%</span>
                          )}
                          <span className={`text-[10px] font-bold px-2.5 py-1 rounded-full ${cfg.color}`}>{cfg.label}</span>
                        </div>
                      </div>
                      {sub.score !== null && (
                        <div className="space-y-1">
                          <Progress value={sub.score} className="h-1.5" />
                        </div>
                      )}
                      {sub.feedback && (
                        <div className="bg-white/60 rounded-xl p-3 border border-black/8">
                          <p className="text-[10px] font-bold text-muted-foreground uppercase mb-1">Mentor Feedback</p>
                          <p className="text-xs text-foreground">{sub.feedback}</p>
                        </div>
                      )}
                    </motion.div>
                  );
                })}
              </div>
            </div>
          )}
        </motion.div>
      </AnimatePresence>

      <ConfirmModal
        open={showConfirm}
        title="Submit Project?"
        description={`Submit "${form.title}" for Module "${form.module}"? Your mentor will review it within 2–3 business days.`}
        confirmLabel="Yes, Submit"
        loading={submitting}
        onConfirm={handleSubmit}
        onCancel={() => setShowConfirm(false)}
      />
    </div>
  );
}